/**
 * D-383: Zero-knowledge account recovery warning.
 *
 * Full-screen interstitial shown before any password reset action.
 * User must explicitly acknowledge data loss risk before proceeding.
 */

import { useState } from 'react';
import { B } from '../constants';

export default function ZeroKnowledgeWarning({ onProceed, onCancel }) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: B.bg, color: B.text,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      <div style={{ maxWidth: 400, textAlign: 'center' }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          border: `2px solid ${B.red}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px', fontSize: 22, color: B.red,
        }}>
          !
        </div>

        <h1 style={{
          fontSize: 18, fontWeight: 600, color: B.text, marginBottom: 20,
          fontFamily: "'IBM Plex Mono', monospace",
        }}>
          Zero-Knowledge Warning
        </h1>

        <div style={{
          fontSize: 14, lineHeight: 1.7, color: B.textSec, marginBottom: 24,
          textAlign: 'left',
        }}>
          <p style={{ marginBottom: 16, color: B.text, fontWeight: 500 }}>
            Fynwell employees have zero access to your data.
          </p>
          <p style={{ marginBottom: 16 }}>
            Password reset will <span style={{ color: B.red, fontWeight: 600 }}>permanently delete all vault data</span> unless
            a cloud backup exists.
          </p>
          <p style={{ fontWeight: 600, color: B.red }}>
            This cannot be undone.
          </p>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.03)', borderRadius: 8,
          border: `1px solid ${B.border}`, padding: '12px 16px',
          marginBottom: 24, textAlign: 'left',
        }}>
          <div style={{ fontSize: 11, color: B.muted, marginBottom: 8, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Before you proceed
          </div>
          <div style={{ fontSize: 13, color: B.textSec, lineHeight: 1.6 }}>
            If you have a monthly cloud backup (Google Drive or iCloud), your data can be
            restored to a new account with biometric verification.
          </div>
        </div>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          fontSize: 12, color: B.textSec, cursor: 'pointer', marginBottom: 24,
          textAlign: 'left', lineHeight: 1.5,
        }}>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={e => setAcknowledged(e.target.checked)}
            style={{ accentColor: B.red, marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
          />
          I understand that resetting my password without a cloud backup will permanently
          delete my vault data, and Fynwell cannot recover it.
        </label>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '12px 14px', borderRadius: 10,
              border: `1px solid ${B.border}`, background: 'transparent',
              color: B.text, fontSize: 13, cursor: 'pointer',
              fontFamily: "'IBM Plex Mono', monospace",
            }}
          >
            Go Back
          </button>
          <button
            onClick={onProceed}
            disabled={!acknowledged}
            style={{
              flex: 1, padding: '12px 14px', borderRadius: 10,
              border: 'none', background: acknowledged ? B.red : B.subtle,
              color: acknowledged ? '#fff' : B.muted, fontSize: 13,
              cursor: acknowledged ? 'pointer' : 'not-allowed',
              fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
              opacity: acknowledged ? 1 : 0.5,
            }}
          >
            Reset Password
          </button>
        </div>
      </div>
    </div>
  );
}
